import { FC, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  School as SchoolIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useScoringStructureHook,
  useDeleteScoringStructureHook,
} from "../../../hooks/useAssessmentHook";
import { TScoringStructure } from "../../../apis/assessment/assessment.interfaces";
import { logger, pages } from "../../../utils/common";
import { useClassHook, useSubjectsHook } from "../../../hooks/useAcademicHook";
import { hasAssessmentPrerequisites } from "./assessment.helpers";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ManageScoringStructureDialog from "./manageScoringStructureDialog";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

const ScoringStructureView: FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useScoringStructureHook();
  const {
    data: classes,
    isLoading: classesLoading,
    error: classesError,
  } = useClassHook();
  const {
    data: subjects,
    isLoading: subjectsLoading,
    error: subjectsError,
  } = useSubjectsHook();
  const deleteStructureMutation = useDeleteScoringStructureHook();
  const { control, watch } = useForm<{ search: string }>({
    defaultValues: { search: "" },
  });
  const searchQuery = watch("search");
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [editingStructure, setEditingStructure] =
    useState<TScoringStructure | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [structureToDelete, setStructureToDelete] =
    useState<TScoringStructure | null>(null);

  const prerequisitesMet = useMemo(
    () => hasAssessmentPrerequisites(classes, subjects),
    [classes, subjects],
  );
  const isLoadingPrerequisites = classesLoading || subjectsLoading;
  const hasPrerequisitesError = classesError || subjectsError;

  const filteredStructures = useMemo(() => {
    if (!data) return [];

    return data.filter((structure) => {
      const matchesSearch =
        structure.className
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        structure.components.some((comp) =>
          comp.name.toLowerCase().includes(searchQuery.toLowerCase()),
        );

      return matchesSearch;
    });
  }, [data, searchQuery]);

  const handleAddStructure = () => {
    setEditingStructure(null);
    setManageDialogOpen(true);
  };

  const handleEditStructure = (structure: TScoringStructure) => {
    setEditingStructure(structure);
    setManageDialogOpen(true);
  };

  const handleDeleteStructure = (structure: TScoringStructure) => {
    setStructureToDelete(structure);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!structureToDelete) return;

    try {
      await deleteStructureMutation.mutateAsync(structureToDelete.id);
      logger.show_success("Scoring structure deleted successfully!");
      setDeleteDialogOpen(false);
      setStructureToDelete(null);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to delete scoring structure. Please try again.",
      );
    }
  };

  const handleManageDialogClose = () => {
    setManageDialogOpen(false);
    setEditingStructure(null);
  };

  if (isLoading) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Scoring Structure`}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="scoring-structure-view"
        >
          <CircularProgress data-testid="scoring-structure-loading-spinner" />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Scoring Structure`}
        />
        <Box p={3} data-testid="scoring-structure-view">
          <Alert severity="error" data-testid="scoring-structure-error">
            Failed to load scoring structures. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  if (hasPrerequisitesError) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Scoring Structure`}
        />
        <Box p={3} data-testid="scoring-structure-view">
          <Alert
            severity="error"
            data-testid="scoring-structure-prerequisites-error"
          >
            Failed to load classes or subjects data. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoadingPrerequisites) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Scoring Structure`}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="scoring-structure-view"
        >
          <CircularProgress data-testid="scoring-structure-loading-spinner" />
        </Box>
      </>
    );
  }

  const showPrerequisitesEmptyState =
    !prerequisitesMet && (!data || data.length === 0);

  return (
    <>
      <DocumentTitle
        title={`${CONSTANTS.title.assessment} - Scoring Structure`}
      />

      <Grid container spacing={3} data-testid="scoring-structure-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="scoring-structure-header"
          >
            <Typography component="h2" variant="h6" fontWeight={600}>
              Scoring structure
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddStructure}
              size="large"
              disabled={!prerequisitesMet}
              data-testid="add-scoring-structure-button"
            >
              Add Scoring Structure
            </Button>
          </Box>
        </Grid>

        {showPrerequisitesEmptyState ? (
          <Grid item xs={12}>
            <Card data-testid="scoring-structure-prerequisites-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="300px"
                  gap={2}
                >
                  <SchoolIcon sx={{ fontSize: 64, color: "text.secondary" }} />
                  <Alert
                    severity="info"
                    icon={<InfoIcon />}
                    sx={{ width: "100%", maxWidth: 600 }}
                    data-testid="scoring-structure-prerequisites-alert"
                  >
                    <Typography variant="h6" gutterBottom>
                      Prerequisites Required
                    </Typography>
                    <Typography variant="body2" paragraph>
                      To configure scoring structures, you must first set up
                      classes and subjects in Academics.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate(`${pages.academics.path}/subjects`)}
                      sx={{ mt: 2 }}
                      startIcon={<SchoolIcon />}
                      data-testid="navigate-to-academic-button"
                    >
                      Go to Academic Module
                    </Button>
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <>
            {/* Search */}
            <Grid item xs={12}>
              <Card data-testid="scoring-structure-filters-card">
                <CardContent>
                  <ControlledTextField
                    control={control}
                    name="search"
                    id="scoring-structure-search"
                    label="Search"
                    isGridItem={false}
                    fullWidth
                    placeholder="Search by class or component name..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ "data-testid": "scoring-structure-search-input" }}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Structure List */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                data-testid="scoring-structures-count"
              >
                Scoring Structures ({filteredStructures.length})
              </Typography>
            </Grid>

            {filteredStructures.length === 0 ? (
              <Grid item xs={12}>
                <Card data-testid="scoring-structure-empty-state">
                  <CardContent>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      minHeight="200px"
                      gap={2}
                    >
                      <SettingsIcon
                        sx={{ fontSize: 64, color: "text.secondary" }}
                      />
                      <Typography variant="h6" color="text.secondary">
                        {data?.length === 0
                          ? "No scoring structures configured"
                          : "No scoring structures match your search criteria"}
                      </Typography>
                      {data?.length === 0 && prerequisitesMet && (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            Get started by adding your first scoring structure.
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddStructure}
                            data-testid="add-scoring-structure-empty-button"
                          >
                            Add Scoring Structure
                          </Button>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TableContainer
                  component={Paper}
                  data-testid="scoring-structure-table-container"
                >
                  <Table data-testid="scoring-structure-table">
                    <TableHead data-testid="scoring-structure-table-head">
                      <TableRow>
                        <TableCell>Class</TableCell>
                        <TableCell>Total Marks</TableCell>
                        <TableCell>Passing Marks</TableCell>
                        <TableCell>Components</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-testid="scoring-structure-table-body">
                      {filteredStructures.map((structure) => (
                        <TableRow
                          key={structure.id}
                          hover
                          data-testid={`scoring-structure-row-${structure.id}`}
                        >
                          <TableCell>
                            <Typography variant="body1" fontWeight="medium">
                              {structure.className || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {structure.totalMarks}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {structure.passingMarks}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              {structure.components.map((comp) => (
                                <Chip
                                  key={comp.id}
                                  label={`${comp.name} (${comp.weightPercentage}%)`}
                                  variant="outlined"
                                  size="small"
                                  data-testid={`component-chip-${comp.id}`}
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleEditStructure(structure)}
                              color="primary"
                              data-testid={`edit-scoring-structure-${structure.id}`}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteStructure(structure)}
                              color="error"
                              data-testid={`delete-scoring-structure-${structure.id}`}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <ManageScoringStructureDialog
        open={manageDialogOpen}
        onClose={handleManageDialogClose}
        scoringStructure={editingStructure}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setStructureToDelete(null);
        }}
        data-testid="delete-scoring-structure-dialog"
      >
        <DialogTitle data-testid="delete-scoring-structure-dialog-title">
          Delete Scoring Structure
        </DialogTitle>
        <DialogContent data-testid="delete-scoring-structure-dialog-content">
          <DialogContentText>
            Are you sure you want to delete the scoring structure for "
            {structureToDelete?.className}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions data-testid="delete-scoring-structure-dialog-actions">
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setStructureToDelete(null);
            }}
            disabled={deleteStructureMutation.isPending}
            data-testid="cancel-delete-scoring-structure-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteStructureMutation.isPending}
            startIcon={
              deleteStructureMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-scoring-structure-button"
          >
            {deleteStructureMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScoringStructureView;
